import { ReactTable } from "@bphxd/ds-core-react";
import { ExternalLink24,Info24 } from "@bphxd/ds-core-react/lib/icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState, useEffect } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/userContextApi";
import "./QNATable.scss";
import QuestionnaireModal from "../Questionnaire/QuestionnaireModal";
import { UncontrolledTooltip } from "reactstrap";

type OrderModel = {
  id: number;
  order_number: string;
  vessel_imo_number: string;
  vessel_name: string;
  cust_code: string;
  cust_acct_name: string;
  country_name: string;
  created_date: string;
  response_status: string;
};

type OpenModal = {
  modalId: string; // Unique ID for each modal instance
  orderData: OrderModel;
  //token: string | null;
};

export default function OrderTable({
  rowData,
}: {
  rowData: OrderModel[];
}) {
  const [randomKeyTable, setRandomKeyTable] = useState<number>(0);
  const [openModals, setOpenModals] = useState<OpenModal[]>([]);
  const navigate = useNavigate();
  const { headerView, setHeaderView } = useUser();
 const token = sessionStorage.getItem("jwt");
  useEffect(() => {
    setRandomKeyTable(Math.floor(Math.random() * 1000));
  }, [rowData]);

  const getStatusStyle = (statusRaw: string) => {
    const status = statusRaw?.toUpperCase();
    const statusMap: any = {
      "OPEN": {
        bg: "#fff3cd",
        color: "#856404",
      },
      "SUBMITTED": {
        bg: "#d4edda",
        color: "#155724",
      },
      "RE-SUBMITTED": {
        bg: "#d4edda",
        color: "#155724",
      },
    };
    return (
      statusMap[status] || {
        bg: "#e2e3e5",
        color: "#383d41",
      }
    );
  };

  // Open new modal - Allow duplicates
  const handleOpenModal = (orderData: OrderModel) => {
    setHeaderView(false);
    const uniqueModalId = `${orderData.order_number}-${Date.now()}-${Math.random()}`;
    setOpenModals((prev) => [...prev, { modalId: uniqueModalId, orderData,token }]);
  };

  // Close specific modal by modalId
  const handleCloseModal = (modalId: string) => {
    setOpenModals((prev) => prev.filter((m) => m.modalId !== modalId));
  };

  const cols: {
    accessorKey?: string;
    header: any;
    cell?: any;
    id?: string;
  }[] = [
    {
      id: "view",
      header: "View",
      cell: ({ row }: any) => {
        return (
          <ExternalLink24
            style={{
              cursor: "pointer",
              transition: "color 0.2s",
              color: "#0078d4",
            }}
            onClick={() => handleOpenModal(row.original)}
          />
        );
      },
    },
    {
      accessorKey: "order_number",
      header: "Order Number",
    },
    {
      accessorKey: "vessel_imo_number",
      header: "Vessel IMO",
    },
    {
      accessorKey: "vessel_name",
      header: "Vessel Name",
    },
    {
      accessorKey: "cust_code",
      header: "Customer Code",
    },
    {
      accessorKey: "cust_acct_name",
      header: "Customer Name",
    },
    {
      accessorKey: "country_name",
      header: "Country",
    },
    {
      accessorKey: "response_status",
      header: () => (<div>
        <div id="download_tooltip">
        <Info24 />Response Status 
        </div>
        <UncontrolledTooltip
                            autohide={false}
                            fade
                            flip
                            placement="bottom"
                            target="download_tooltip"
                            style={{backgroundColor:"darkgray"}}
                            popperClassName="ft-size"
                          >
                            <div className="tooltip-content">
    <div><span className="open">OPEN</span> – Customer has not yet answered the questions.</div>

    <div><span className="submitted">SUBMITTED</span> – Customer answered the question for the first time.</div>

    <div><span className="default-text">REMARKED</span> – OM provided remarks for the first time.</div>

    <div><span className="submitted">RE-SUBMITTED</span> – Customer provided an answer more than once (after remarks/review).</div>

    <div><span className="default-text">RE-REMARKED</span> – OM provided remarks more than once.</div>
    <div><span className="default-text">APPROVED</span> – OM Approved the questionnaire & boundary conditions.</div>
  </div>
                          </UncontrolledTooltip>
      </div>
      ),
      cell: (props: any) => {
        const status = props.getValue();
        const style = getStatusStyle(status);
        return (
          <span
            style={{
              padding: "4px 10px",
              borderRadius: "12px",
              backgroundColor: style.bg,
              color: style.color,
              fontWeight: 500,
              fontSize: "12px",
            }}
          >
            {status}
          </span>
        );
      },
    },
    {
      accessorKey: "created_date",
      header: "Created Date",
      cell: (props: any) => {
        return (
          <div>
            {props.getValue()
              ? moment(props.getValue()).format("DD MMM YYYY")
              : ""}
          </div>
        );
      },
    },
  ];

  const columns = useMemo<ColumnDef<OrderModel>[]>(() => cols, []);

  return (
    <div className="container-custom">
      <ReactTable<OrderModel>
        tableClassName="table-main-class"
        columns={columns}
        data={rowData}
        striped={true}
        responsive={true}
        key={randomKeyTable}
      />

      {/* Render multiple modals - Allow duplicates */}
      {openModals.map((modal, index) => (
        <QuestionnaireModal
          key={modal.modalId}
          isOpen={true}
          onClose={() => handleCloseModal(modal.modalId)}
          orderData={modal.orderData}
          //token={modal.token}
          modalIndex={index}
        />
      ))}
    </div>
  );
}